import packageJson from '../package.json';

const WIDGET_VERSION = packageJson.version;

export interface StreamEventHandlers {
  onResponseChunk?: (content: string) => void;
  onCompleteResponse?: (content: string) => void;
  onSuggestedQuestions?: (questions: string[]) => void;
  onSources?: (sources: SourceDocument[]) => void;
  onMetadata?: (metadata: ResponseMetadata) => void;
  onDone?: () => void;
  onError?: (error: string) => void;
  onContactForm?: (contactForm: ContactFormPayload) => void;
  onQualificationOptions?: (options: QualificationOptionsPayload) => void;
  /** A lead question offered as the last suggestion chip ("Your turn"). */
  onYourTurn?: (question: QualificationOptionsPayload) => void;
}

/**
 * How the visitor sent a message — the `query_type` stored on the chat and
 * shown in the admin's transcripts.
 */
export const QUERY_TYPE = {
  /** Clicked a suggested question. */
  SUGGESTED: 0,
  /** Typed it. */
  TYPED: 1,
  /** Answered a qualification question by clicking one of its options. */
  QUALIFICATION_OPTION: 2,
  /** Picked "Something else" and typed their own answer. */
  QUALIFICATION_CUSTOM: 3,
} as const;

/**
 * The sentinel for "Something else".
 *
 * Appended by the backend rather than written by the model, so it is always
 * present, always last, and always this exact string — the custom text input
 * keys off it.
 */
export const CUSTOM_OPTION_VALUE = '__custom__';

export interface QualificationOption {
  value: string;
  label: string;
  /** One short clause saying what picking it means. Often absent. */
  description?: string;
}

/**
 * A discovery question the reply just asked, with its clickable answers.
 *
 * Arrives after the answer has finished streaming, and never on the same turn
 * as `suggested_questions` — the backend suppresses those whenever it asks a
 * question with options, so only one row of choices sits under a reply.
 *
 * `answer_type: 'text'` is the same question with answer buttons switched off
 * (the admin's Tappable setting): the reply asked it in words, `options` is
 * empty, and the visitor answers by typing an ordinary message. It is held in
 * the same slot so everything that keeps suggestions away from an open
 * question — and restores it after a refresh — applies to both.
 */
export interface QualificationOptionsPayload {
  question_id: string;
  question: string;
  answer_type: 'single_choice_with_custom' | 'multi_choice_with_custom' | 'text';
  options: QualificationOption[];
}

/**
 * The options payload of `qualification_options` and `your_turn`, or null when
 * it cannot be answered: a malformed or empty option list would leave the
 * visitor with a question and nothing to click.
 */
function readOptionsPayload(payload: any): QualificationOptionsPayload | null {
  if (!payload || typeof payload !== 'object') return null;
  const options = (payload.options ?? [])
    .map((o: any) => ({
      value: String(o?.value ?? ''),
      label: String(o?.label ?? ''),
      description: o?.description ? String(o.description) : undefined,
    }))
    .filter((o: QualificationOption) => o.value && o.label);
  if (options.length < 2) return null;
  return {
    question_id: String(payload.question_id ?? ''),
    question: String(payload.question ?? ''),
    answer_type: payload.answer_type === 'multi_choice_with_custom'
      ? 'multi_choice_with_custom'
      : 'single_choice_with_custom',
    options,
  };
}

/** Whether this question renders answer buttons (false for a typed-answer one). */
export function hasAnswerButtons(q: QualificationOptionsPayload | null | undefined): boolean {
  return Boolean(q && q.answer_type !== 'text' && (q.options?.length ?? 0) >= 2);
}

/** Whether the reply asked a question the visitor answers by typing. */
export function isTypedQuestion(q: QualificationOptionsPayload | null | undefined): boolean {
  return Boolean(q && q.answer_type === 'text');
}

/**
 * What the visitor did with those options, sent back on the next turn.
 *
 * The answer text also goes in `question`, so the whole pipeline treats the
 * turn as an ordinary message. This carries the question's identity, which
 * text alone cannot — it is what lets the answer be recorded against the
 * question it belongs to instead of being matched by guesswork.
 */
export interface QualificationAnswerPayload {
  question_id: string;
  answer_type: string;
  selected: Array<{ value: string; label: string }>;
  is_custom: boolean;
  custom_text?: string;
}

export interface SourceDocument {
  title: string;
  url: string;
  relevance: number;
}

export interface ResponseMetadata {
  processing_time?: number;
  tokens_used?: number;
  model?: string;
}

export interface ContactFormField {
  field_name: string;
  field_label: string;
  field_type: 'text' | 'email' | 'tel' | 'textarea';
  required: boolean;
  placeholder?: string;
  order?: number;
  value?: string;
}

export interface ContactFormPayload {
  message: string;
  original_query: string;
  fields: ContactFormField[];
}

export interface StreamEventData {
  /**
   * `complete_response`, `sources` and `metadata` are legacy: the sales-bot
   * stream never sends them. They stay in the union so a deployment still
   * pointing at the old endpoint keeps working unchanged.
   */
  type: 'response_chunk' | 'complete_response' | 'suggested_questions' | 'suggestions' | 'sources' | 'metadata' | 'contact_form' | 'qualification_options' | 'qualification_question' | 'your_turn' | 'done' | 'error';
  /** Legacy endpoint puts the payload here. */
  content?: any;
  /** Sales-bot endpoint puts structured payloads here. */
  data?: any;
  questions?: any;
  suggestions?: any;
}

export class ChatAPI {
  private apiUrl: string;
  private chatEndpoint: 'sales-bot' | 'chat';

  /**
   * `chatEndpoint` picks the backend. Both are supported and the event
   * handling below reads either shape, so this only decides the URL.
   *
   * Defaults to the sales-bot pipeline; pass 'chat' for the legacy one.
   */
  constructor(apiUrl: string, chatEndpoint: 'sales-bot' | 'chat' = 'sales-bot') {
    this.apiUrl = apiUrl.replace(/\/$/, '');
    this.chatEndpoint = chatEndpoint;
  }

  async streamQuery(
    sessionId: string,
    question: string,
    handlers: StreamEventHandlers,
    queryType?: number,
    currentPage?: string,
    campaignRef?: string | null,
    signal?: AbortSignal,
    qualificationAnswer?: QualificationAnswerPayload
  ): Promise<void> {
    const requestPayload = {
      session_id: sessionId,
      question: question,
      // Present only when this message came from clicking a qualification
      // option. The answer text is already in `question`, so the pipeline
      // treats the turn as ordinary; this just names the question answered.
      ...(qualificationAnswer && { qualification_answer: qualificationAnswer }),
      ...(queryType !== undefined && { query_type: queryType }),
      ...(currentPage && { current_page: currentPage }),
      // Per-recipient campaign code from the link's ?c= parameter. Sent on
      // EVERY message, not just the first — the backend stores it per chat,
      // and a conversation attributed only on its opening turn would lose the
      // rest of itself.
      ...(campaignRef && { campaign_ref: campaignRef })
    };

    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let buffer = '';

    // The legacy endpoint re-sent the whole answer as `complete_response`, and
    // the widget uses that to mark the turn finished. The sales-bot stream
    // does not — the client already has every chunk — so the answer is
    // accumulated here and handed over once, on `done`. Emitting it from the
    // API layer keeps the widget's handler contract unchanged.
    let accumulated = '';
    let completeSent = false;
    let doneSent = false;

    const emitComplete = (): void => {
      if (completeSent) return;
      completeSent = true;
      if (handlers.onCompleteResponse && accumulated.length > 0) {
        handlers.onCompleteResponse(accumulated);
      }
    };

    const emitDone = (): void => {
      if (doneSent) return;
      doneSent = true;
      if (handlers.onDone) {
        handlers.onDone();
      }
    };

    // One SSE line. Extracted so the end-of-stream flush below runs the exact
    // same handling as the loop — a second copy would inevitably drift.
    const handleLine = (rawLine: string): void => {
      const line = rawLine.trimStart();
      if (!line.startsWith('data:')) return;

      const jsonString = line.slice(5).trim();
      if (!jsonString) return;

      try {
        const data: StreamEventData = JSON.parse(jsonString);

        switch (data.type) {
          case 'response_chunk':
            if (typeof data.content === 'string') {
              accumulated += data.content;
              if (handlers.onResponseChunk) {
                handlers.onResponseChunk(data.content);
              }
            }
            break;

          case 'complete_response':
            // Legacy endpoint only.
            if (typeof data.content === 'string') {
              completeSent = true;
              if (handlers.onCompleteResponse) {
                handlers.onCompleteResponse(data.content);
              }
            }
            break;

          case 'suggested_questions':
          case 'suggestions': {
            if (!handlers.onSuggestedQuestions) break;
            const payload = data.data ?? data.content ?? data;
            let raw: any[] = [];
            if (Array.isArray(payload)) {
              raw = payload;
            } else if (payload && typeof payload === 'object') {
              if (Array.isArray(payload.questions)) {
                raw = payload.questions;
              } else if (Array.isArray(payload.suggestions)) {
                raw = payload.suggestions;
              }
            }
            if (raw.length === 0) {
              if (Array.isArray(data.questions)) {
                raw = data.questions;
              } else if (Array.isArray(data.suggestions)) {
                raw = data.suggestions;
              }
            }
            const questions = raw
              .map((q) => (typeof q === 'string' ? q : q?.question || q?.text || q?.label || ''))
              .filter((q): q is string => typeof q === 'string' && q.trim().length > 0);
            if (questions.length > 0) handlers.onSuggestedQuestions(questions);
            break;
          }

          case 'sources':
            if (handlers.onSources && Array.isArray(data.content)) {
              handlers.onSources(data.content);
            }
            break;

          case 'metadata':
            if (handlers.onMetadata && typeof data.content === 'object') {
              handlers.onMetadata(data.content);
            }
            break;

          case 'contact_form': {
            if (!handlers.onContactForm) break;
            // Legacy sends the whole payload on `content`. The sales-bot
            // endpoint sends `data: {mode, fields}`, and its field keys are
            // name/label/type where this widget expects
            // field_name/field_label/field_type — so map rather than rename
            // everything downstream.
            const payload = data.data ?? data.content;
            if (!payload || typeof payload !== 'object') break;

            const fields = (payload.fields ?? []).map((f: any) => ({
              field_name: f.field_name ?? f.name,
              field_label: f.field_label ?? f.label,
              field_type: f.field_type ?? f.type ?? 'text',
              required: !!f.required,
              placeholder: f.placeholder,
              order: f.order,
              value: f.value,
            }));

            handlers.onContactForm({
              message: payload.message ?? '',
              original_query: payload.original_query ?? question,
              fields,
            } as ContactFormPayload);
            break;
          }

          case 'qualification_options': {
            if (!handlers.onQualificationOptions) break;
            // Only render a row that can actually be answered.
            const parsed = readOptionsPayload(data.data ?? data.content);
            if (parsed) handlers.onQualificationOptions(parsed);
            break;
          }

          case 'your_turn': {
            // A lead question offered as the last suggestion chip, after the
            // (now two) suggestions. The reply did not ask it: the chip shows
            // the question, and its options open only when it is tapped. A
            // tapped option goes back exactly like a qualification answer.
            if (!handlers.onYourTurn) break;
            const parsed = readOptionsPayload(data.data ?? data.content);
            if (parsed && parsed.question_id && parsed.question) handlers.onYourTurn(parsed);
            break;
          }

          case 'qualification_question': {
            // Answer buttons are off, so the reply asked its question in words
            // and the backend sent no suggestions on purpose. Held like an
            // options row with no options: nothing renders under the reply,
            // and fallback suggestions are not added in place of the missing
            // ones — they would pull the visitor away from answering. Their
            // typed reply goes back as an ordinary message; the backend knows
            // which question is open.
            if (!handlers.onQualificationOptions) break;
            const payload = data.data ?? data.content;
            if (!payload || typeof payload !== 'object' || !payload.question_id) break;
            handlers.onQualificationOptions({
              question_id: String(payload.question_id),
              question: '',
              answer_type: 'text',
              options: [],
            });
            break;
          }

          case 'done':
            // Before onDone: the widget treats the complete response as the
            // signal that the turn is finished, so a `done` that arrived first
            // would leave it streaming forever.
            emitComplete();
            emitDone();
            break;

          case 'error':
            if (handlers.onError && typeof data.content === 'string') {
              handlers.onError(data.content);
            }
            break;
        }
      } catch (parseError) {
        console.error('Error parsing SSE data:', parseError);
      }
    };

    try {
      const response = await fetch(`${this.apiUrl}/${this.chatEndpoint}/query/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-Version': WIDGET_VERSION,
        },
        body: JSON.stringify(requestPayload),
        signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const decoder = new TextDecoder('utf-8');
      reader = response.body.getReader();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        // The last element is whatever came after the final newline — an
        // incomplete line — so it stays buffered for the next read.
        buffer = lines.pop() || '';

        for (const line of lines) {
          handleLine(line);
        }
      }

      // Flush. Without this, an event still sitting in `buffer` when the
      // stream closes is discarded — which is exactly what made the widget
      // stop mid-answer while the backend had sent the whole thing. It
      // happens whenever the final event arrives without its trailing
      // newline, or the connection ends right after the last chunk.
      buffer += decoder.decode();
      for (const line of buffer.split('\n')) {
        handleLine(line);
      }
      buffer = '';

      // A stream that ended without `done` — a dropped connection, a proxy
      // timeout — still has to finalise, or the widget types forever waiting
      // for a completion signal that is not coming.
      emitComplete();
      emitDone();
    } catch (error) {
      if (signal?.aborted || (error instanceof Error && error.name === 'AbortError')) {
        // Intentional cancellation; exit silently without notifying onError
        return;
      }
      if (handlers.onError) {
        handlers.onError(error instanceof Error ? error.message : 'Unknown streaming error');
      }
      throw error;
    } finally {
      // Release the lock even on an aborted stream, so the connection is not
      // pinned open by a reader nobody is draining any more.
      if (reader) {
        try {
          reader.releaseLock();
        } catch {
          // Already released — nothing to do.
        }
      }
    }
  }

  async fetchInitialSuggestions(isFirstVisit: boolean = true, pageUrl?: string): Promise<string[]> {
    try {
      const url = new URL(`${this.apiUrl}/chat/suggestions`);
      url.searchParams.append('is_first_visit', String(isFirstVisit));
      if (pageUrl) {
        url.searchParams.append('page_url', pageUrl);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-Version': WIDGET_VERSION,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const raw = Array.isArray(result)
        ? result
        : (result?.suggestions || result?.data?.suggestions || result?.data?.questions || result?.data || result?.questions || []);
      return (Array.isArray(raw) ? raw : [])
        .map((q) => (typeof q === 'string' ? q : q?.question || q?.text || q?.label || ''))
        .filter((q): q is string => typeof q === 'string' && q.trim().length > 0);
    } catch (error) {
      console.error('Error fetching initial suggestions:', error);
      return [];
    }
  }

  async submitChatFeedback(sessionId: string, feedback: 0 | 1): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/chat/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-Version': WIDGET_VERSION,
        },
        body: JSON.stringify({
          session_id: sessionId,
          feedback
        })
      });

      const result = await response.json();
      return result.status === 'success';
    } catch (error) {
      console.error('Error submitting feedback:', error);
      return false;
    }
  }

  async submitContactForm(payload: {
    session_id: string;
    user_query: string;
    contact_data: Record<string, string>;
  }): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${this.apiUrl}/chat/contact-submission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-Version': WIDGET_VERSION,
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success === true || result.status === 'success') {
        return {
          success: true,
          message: result.data?.message || result.message || 'Thank you for reaching out! Our team will contact you shortly.'
        };
      }

      return {
        success: false,
        error: result.message || result.error || 'Submission failed'
      };
    } catch (error) {
      console.error('Error submitting contact form:', error);
      return {
        success: false,
        error: 'Network error occurred'
      };
    }
  }

  /**
   * Ask the backend to email this visitor a summary of their own conversation.
   *
   * Whose chats get summarised is decided SERVER-SIDE, from two things:
   *
   *  - the **IP address**, read off the network connection. It is deliberately
   *    NOT sent in this payload: a browser cannot know its own public IP, and
   *    anything it claimed could be edited in devtools to request a stranger's
   *    history.
   *  - the **campaign code** (`?c=r_...`), which the browser genuinely does
   *    know, so it is sent. The backend validates it against the live roster
   *    and ignores anything forged. If it is missing, the backend recovers it
   *    from the chats already written for this `session_id`.
   *
   * The email address is only where the summary is delivered — never how the
   * chats are found — so typing someone else's address cannot surface their
   * conversation.
   *
   * The response echoes `identifier_type` ("ip" or "recipient") and
   * `identifier`, so the caller can confirm which history was used.
   *
   * Resolves as soon as the request is accepted; the summary is written and
   * sent in the background, so this does not wait on an LLM call.
   */
  async requestChatSummary(payload: {
    session_id: string;
    name?: string;
    email: string;
    campaign_ref?: string | null;
  }): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${this.apiUrl}/chat-summary/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-Version': WIDGET_VERSION,
        },
        body: JSON.stringify({
          session_id: payload.session_id,
          email: payload.email,
          ...(payload.name && { name: payload.name }),
          ...(payload.campaign_ref && { campaign_ref: payload.campaign_ref }),
        }),
      });

      const result = await response.json();

      if (result.success === true) {
        return {
          success: true,
          message:
            result.data?.message ||
            `Your conversation summary is on its way to ${payload.email}.`,
        };
      }

      // The backend's refusals are written for the visitor (no chats yet,
      // rate limited, email turned off), so its message is shown as-is rather
      // than replaced with a generic failure.
      return {
        success: false,
        error:
          result.error?.message ||
          result.message ||
          'We could not send your summary. Please try again.',
      };
    } catch (error) {
      console.error('Error requesting chat summary:', error);
      return {
        success: false,
        error: 'Network error occurred',
      };
    }
  }

}
